// The three comparators every sorted table in this folder is built out of.
//
// They were a verbatim copy in offersSort.ts and marketSort.ts — and byText a
// third time in traderSort.ts — and had already started to drift: one byText
// docstring still said the rule covers nicks and the other had dropped that,
// while the module that dropped it is the one that went on to sort nicks.
// Shared here for the reason LotItems.tsx records — two copies of one rule
// drift before anyone notices, and an ordering that quietly disagrees with its
// twin is exactly the kind of wrong nobody reports.
import type { Mode } from "../api/types";

/** Which way "best" runs for a mode. A buy is what someone will pay, so more is
 *  better; a sell is what they ask and a craft is what they charge, so less is.
 *  This is the one piece of market meaning in the file, and it is why these
 *  cannot be plain column comparators.
 *
 *  Typed on the whole `Mode` rather than on `OfferMode`, so both callers pass
 *  what they already hold. A package has no "best" side to run — a lot is sold
 *  whole — and falls in with the asks, which is the harmless answer. */
export function bestFirst(mode: Mode): 1 | -1 {
  return mode === "buy" ? -1 : 1;
}

/** Case-insensitive, because item names and trader nicks are both CITEXT on the
 *  server and both are ordered through here.
 *
 *  What `sensitivity: "base"` buys is that case-only variants compare *equal* —
 *  "Coal".localeCompare("coal") is 1 plain and 0 with the option — which is the
 *  comparison CITEXT makes, so a list ordered here agrees with every list the
 *  database produces. It is not about byte order: "zephyr" already sorts after
 *  "Ashenvale" with the option, without it, and under a plain `<`. */
export function byText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

/** Nulls last, always. A price the scanner could not read is not a cheap one,
 *  and sorting it as 0 would park a failed read at the top of a sell page
 *  reading as the best price there is. */
export function byPrice(a: number | null, b: number | null, direction: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return (a - b) * direction;
}
